import { TFunction } from "i18next";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import {
    resolveDetailedActivityEventDetail,
    resolveDetailedActivityEventLabel,
} from "../../Components/DetailedActivityStream/DetailedActivityStream";
import { RESOURCE_LOCATION } from "../../Constants/resourceLocations";
import { CommentService, IComment } from "../../Services/CommentService";
import {
    IArticleCategoryLink,
    IKnowledgeArticleEvent,
    IKnowledgeArticleModel,
} from "../../Services/KnowledgeArticleService";
import { IKnowledgeArticleSectionModel } from "../../Services/KnowledgeArticleSectionService";
import {
    IMediaAssetModel,
    MEDIA_TYPE,
    MediaAssetService,
    isImageAttachment,
} from "../../Services/MediaAssetService";
import { sanitizePdfFilename } from "../Project/projectPdfExportShared";
import { buildKnowledgeArticlePdf } from "./knowledgeArticlePdfExport";

interface IExportKnowledgeArticleFullArchiveParams {
    articleUuid: string;
    article: IKnowledgeArticleModel;
    categories: IArticleCategoryLink[];
    sections: IKnowledgeArticleSectionModel[];
    mediaAssets: IMediaAssetModel[];
    events: IKnowledgeArticleEvent[];
    mediaAssetService: MediaAssetService;
    t: TFunction;
}

const escapeHtml = (value: string): string =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

const formatArchiveWhen = (value: string | undefined): string => {
    if (!value) {
        return "—";
    }
    return new Date(value).toLocaleString();
};

const sanitizeZipFilename = (name: string): string => {
    const sanitized = name
        .trim()
        .replace(/[\\/:*?"<>|]+/g, "_")
        .replace(/\s+/g, " ");
    return sanitized || "file";
};

const ensureExtension = (name: string, extension: string): string => {
    const sanitized = sanitizeZipFilename(name);
    const suffix = `.${extension}`;
    if (sanitized.toLowerCase().endsWith(suffix)) {
        return sanitized;
    }
    return `${sanitized}${suffix}`;
};

const uniqueZipFilename = (used: Set<string>, raw: string): string => {
    const sanitized = sanitizeZipFilename(raw);
    const key = sanitized.toLowerCase();
    if (!used.has(key)) {
        used.add(key);
        return sanitized;
    }
    const dot = sanitized.lastIndexOf(".");
    const base = dot > 0 ? sanitized.slice(0, dot) : sanitized;
    const ext = dot > 0 ? sanitized.slice(dot) : "";
    let index = 2;
    let next = `${base}-${index}${ext}`;
    while (used.has(next.toLowerCase())) {
        index += 1;
        next = `${base}-${index}${ext}`;
    }
    used.add(next.toLowerCase());
    return next;
};

const wrapArchiveHtml = (title: string, exportedOn: string, body: string): string => `<!DOCTYPE html>
<html lang="nl">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    body { margin: 24px; font-family: "Noto Sans", Arial, sans-serif; color: #0f172a; background: #fff; }
    h1 { font-size: 1.35rem; margin: 0 0 0.35rem; }
    .meta { color: #64748b; font-size: 0.85rem; margin-bottom: 1.5rem; }
    article { border: 1px solid #e2e8f0; border-radius: 10px; padding: 0.9rem 1rem; margin: 0 0 0.75rem; }
    .label { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.02em; text-transform: uppercase; color: #1d4ed8; margin: 0 0 0.35rem; }
    .text { white-space: pre-wrap; margin: 0 0 0.6rem; }
    .empty { color: #64748b; }
    footer { color: #64748b; font-size: 0.8rem; }
  </style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <p class="meta">${escapeHtml(exportedOn)}</p>
  ${body}
</body>
</html>
`;

const buildCommunicationsHtml = (title: string, comments: IComment[], t: TFunction): string => {
    const exportedOn = t("knowledgeBase.exportFull.exportedOn", { date: formatArchiveWhen(new Date().toISOString()) });
    const sorted = [...comments].sort((left, right) => {
        const leftTs = left.createdAt ? new Date(left.createdAt).getTime() : 0;
        const rightTs = right.createdAt ? new Date(right.createdAt).getTime() : 0;
        return leftTs - rightTs;
    });
    const body = sorted.length === 0
        ? `<p class="empty">${escapeHtml(t("knowledgeBase.exportFull.noCommunications"))}</p>`
        : sorted.map((comment) => {
            const author = comment.createdBy?.name || comment.createdBy?.code || t("knowledgeBase.exportFull.unknownAuthor");
            return `<article>
    <p class="label">${escapeHtml(t("knowledgeBase.comments"))}</p>
    <p class="text">${escapeHtml(comment.body || "")}</p>
    <footer>${escapeHtml(t("knowledgeBase.exportFull.byline", {
        author,
        date: formatArchiveWhen(comment.createdAt),
    }))}</footer>
  </article>`;
        }).join("\n");
    return wrapArchiveHtml(title, exportedOn, body);
};

const buildActivityHtml = (title: string, events: IKnowledgeArticleEvent[], t: TFunction): string => {
    const exportedOn = t("knowledgeBase.exportFull.exportedOn", { date: formatArchiveWhen(new Date().toISOString()) });
    const sorted = [...events].sort((left, right) => {
        const leftTs = left.createdAt ? new Date(left.createdAt).getTime() : 0;
        const rightTs = right.createdAt ? new Date(right.createdAt).getTime() : 0;
        return leftTs - rightTs;
    });
    const body = sorted.length === 0
        ? `<p class="empty">${escapeHtml(t("knowledgeBase.exportFull.noActivity"))}</p>`
        : sorted.map((event) => {
            const label = resolveDetailedActivityEventLabel(event.eventType || "message", t);
            const detail = resolveDetailedActivityEventDetail({
                uuid: event.uuid,
                eventType: event.eventType || "message",
                payload: event.payload,
                actorName: event.actorName,
                createdBy: event.createdBy,
                createdAt: event.createdAt,
            });
            const author = event.actorName || t("knowledgeBase.exportFull.unknownAuthor");
            return `<article>
    <p class="label">${escapeHtml(label)}</p>
    ${detail ? `<p class="text">${escapeHtml(detail)}</p>` : ""}
    <footer>${escapeHtml(t("knowledgeBase.exportFull.byline", {
        author,
        date: formatArchiveWhen(event.createdAt),
    }))}</footer>
  </article>`;
        }).join("\n");
    return wrapArchiveHtml(title, exportedOn, body);
};

const fetchAssetBlob = async (
    asset: IMediaAssetModel,
    mediaAssetService: MediaAssetService
): Promise<Blob | null> => {
    const tryContent = async (): Promise<Blob | null> => {
        try {
            return await mediaAssetService.fetchContent(asset.uuid);
        } catch {
            return null;
        }
    };
    // Straight from the bucket (its CORS policy allows reading a signed URL from script). A photo or video
    // already has its URL in the media list; a plain attachment gets one here.
    const trySignedUrl = async (): Promise<Blob | null> => {
        try {
            const url = asset.originalUrl ?? (await mediaAssetService.fetchDownloadUrl(asset.uuid)).data.data;
            if (!url) {
                return null;
            }
            const response = await fetch(url);
            if (!response.ok) {
                return null;
            }
            return await response.blob();
        } catch {
            return null;
        }
    };
    if (asset.mediaType === MEDIA_TYPE.PHOTO || isImageAttachment(asset)) {
        return (await tryContent()) ?? (await trySignedUrl());
    }
    return (await trySignedUrl()) ?? (await tryContent());
};

export const exportKnowledgeArticleFullArchive = async ({
    articleUuid,
    article,
    categories,
    sections,
    mediaAssets,
    events,
    mediaAssetService,
    t,
}: IExportKnowledgeArticleFullArchiveParams): Promise<void> => {
    const articleTitle = article.title?.trim() || t("knowledgeBase.exportFull.untitled");
    const root = sanitizePdfFilename(articleTitle);
    const attachmentsFolder = sanitizeZipFilename(t("knowledgeBase.exportFull.attachmentsFolder"));
    const commentService = new CommentService();
    const [pdf, commentResponse] = await Promise.all([
        buildKnowledgeArticlePdf({
            article,
            categories,
            sections,
            mediaAssets,
            mediaAssetService,
            t,
        }),
        commentService.fetchByLocation(RESOURCE_LOCATION.KNOWLEDGE_ARTICLE, articleUuid),
    ]);
    const comments = Array.isArray(commentResponse.data.data) ? commentResponse.data.data : [];
    const zip = new JSZip();
    zip.file(pdf.filename, pdf.blob);
    zip.file(
        ensureExtension(t("knowledgeBase.exportFull.communicationsFile"), "html"),
        new Blob(
            [buildCommunicationsHtml(t("knowledgeBase.exportFull.communicationsTitle", { title: articleTitle }), comments, t)],
            { type: "text/html;charset=utf-8" }
        )
    );
    zip.file(
        ensureExtension(t("knowledgeBase.exportFull.activityFile"), "html"),
        new Blob(
            [buildActivityHtml(t("knowledgeBase.exportFull.activityTitle", { title: articleTitle }), events, t)],
            { type: "text/html;charset=utf-8" }
        )
    );

    const usedNames = new Set<string>();
    const skipped: string[] = [];
    for (const asset of mediaAssets) {
        const blob = await fetchAssetBlob(asset, mediaAssetService);
        const filename = uniqueZipFilename(usedNames, asset.originalFilename || asset.uuid);
        if (!blob) {
            skipped.push(filename);
            continue;
        }
        zip.file(`${attachmentsFolder}/${filename}`, blob);
    }
    if (mediaAssets.length === 0) {
        zip.file(
            `${attachmentsFolder}/${ensureExtension(t("knowledgeBase.exportFull.attachmentsEmptyFile"), "txt")}`,
            t("knowledgeBase.export.noAttachments")
        );
    } else if (skipped.length > 0) {
        zip.file(
            `${attachmentsFolder}/${ensureExtension(t("knowledgeBase.exportFull.skippedFile"), "txt")}`,
            `${t("knowledgeBase.exportFull.skippedNote")}\n\n${skipped.join("\n")}`
        );
    }

    const archive = await zip.generateAsync({ type: "blob" });
    saveAs(archive, `${root}_${t("knowledgeBase.exportFull.filenameSuffix")}.zip`);
};
